/* eslint-disable react-hooks/rules-of-hooks */
import { ReactElement } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Category, DrugItem, SubCategory, DrugPlateField } from 'src/api/types';

import trns from './trns';

const carIcon = 'drug/car.svg';
const alcoholIcon = 'drug/alcohol.svg';
const alergyIcon = 'drug/alergy.svg';
const childIcon = 'drug/child.svg';
const diabetIcon = 'drug/diabet.svg';
const motherIcon = 'drug/mother.svg';
const pregnancyIcon = 'drug/pregnancy.svg';
const receptIcon = 'drug/recept.svg';
const temperatureIcon = 'drug/temperature.svg';

export type InstructionOption = {
  id: string;
  href: string;
  name: string;
  value: string | number | ReactElement;
};
export type FAQOption = {
  id: string;
  name: string | ReactElement;
  value: string | number | ReactElement | DrugPlateField;
};

export type DrugValueField = {
  title: ReactElement | string;
  value: ReactElement | string | number;
  status?: string;
  image?: {
    src: string;
    width: number;
    height: number;
  };
};

export const getDrugCategory = (
  drug: DrugItem,
  categoryList: Category[]
): { category: Category; subCategory: SubCategory } | null => {
  let result = null;

  categoryList.forEach((category) => {
    const subCategory = category.subCategories?.find(
      (subCategoryItem) => subCategoryItem.id === drug?.categoryId
    );
    if (subCategory) {
      result = { category, subCategory };
    }
  });

  return result;
};

const getPlateIcon = (key: string) => {
  switch (key) {
    case 'forAllergic':
      return {
        src: alergyIcon,
        width: 26,
        height: 26
      };
    case 'forDiabetic':
      return {
        src: diabetIcon,
        width: 16,
        height: 26
      };
    case 'forDrivers':
      return {
        src: carIcon,
        width: 26,
        height: 14.6
      };
    case 'forPregnant':
      return {
        src: pregnancyIcon,
        width: 26,
        height: 23.5
      };
    case 'forMothers':
      return {
        src: motherIcon,
        width: 26,
        height: 28.2
      };
    case 'forChildren':
      return {
        src: childIcon,
        width: 26,
        height: 24.3
      };
    case 'withAlcohol':
      return {
        src: alcoholIcon,
        width: 23,
        height: 26
      };
    case 'withRecipe':
      return {
        src: receptIcon,
        width: 26,
        height: 26
      };
    case 'storageTemperature':
      return {
        src: temperatureIcon,
        width: 23,
        height: 26
      };

    default:
      return null;
  }
};

export const generateDescriptionWhoCanUse = (
  drug: DrugItem
): DrugValueField[] => {
  const platesValues = [
    { key: 'forAllergic', ...drug.forAllergic },
    { key: 'forDiabetic', ...drug.forDiabetic },
    { key: 'forDrivers', ...drug.forDrivers },
    { key: 'forPregnant', ...drug.forPregnant },
    { key: 'forMothers', ...drug.forMothers },
    { key: 'forChildren', ...drug.forChildren }
  ]
    // removes null
    .filter((n) => n);

  return platesValues
    .map((plate) => {
      if (plate.text) {
        return {
          title: trns[plate.key] ? (
            <FormattedMessage {...trns[plate.key]} />
          ) : (
            ''
          ),
          status: plate.color,
          value: plate.text,
          image: getPlateIcon(plate.key)
        };
      }
      return null;
    })
    .filter((n) => n);
};

export const generateDescriptionSpecialties = (
  drug: DrugItem
): DrugValueField[] => {
  const platesValues = [
    { key: 'withAlcohol', ...drug.withAlcohol },
    { key: 'withRecipe', ...drug.withRecipe },
    { key: 'storageTemperature', ...drug.storageTemperature }
  ]
    // removes null
    .filter((n) => n);

  return platesValues
    .map((plate) => {
      if (plate.text) {
        return {
          title: trns[plate.key] ? (
            <FormattedMessage {...trns[plate.key]} />
          ) : (
            ''
          ),
          status: plate.color,
          value: plate.text,
          image: getPlateIcon(plate.key)
        };
      }
      return null;
    })
    .filter((n) => n);
};

export const generateInstructionsFields = (
  drug: DrugItem,
  subCategoryName: string
): DrugValueField[] => {
  return [
    {
      title: <FormattedMessage {...trns.category} />,
      value: subCategoryName
    },
    {
      title: <FormattedMessage {...trns.doze} />,
      value: drug?.doze
    },
    {
      title: <FormattedMessage {...trns.maker} />,
      value: drug?.maker
    },
    {
      title: <FormattedMessage {...trns.makerCountry} />,
      value: drug?.makerCountry
    },
    {
      title: <FormattedMessage {...trns.tradeName} />,
      value: drug?.tradeName
    },
    {
      title: <FormattedMessage {...trns.form} />,
      value: drug?.form
    },
    {
      title: <FormattedMessage {...trns.expiration} />,
      value: drug?.expiration
    },
    {
      title: <FormattedMessage {...trns.component} />,
      value: drug?.component
    },
    {
      title: <FormattedMessage {...trns.quantityInPack} />,
      value: drug?.quantityInPack
    },
    {
      title: <FormattedMessage {...trns.inputType} />,
      value: drug?.inputType
    },
    {
      title: <FormattedMessage {...trns.morionCode} />,
      value: drug?.morionCode
    },
    {
      title: <FormattedMessage {...trns.atx} />,
      value: drug?.atx
    }
  ].filter((n) => n.value);
};

export const generateDrugPageNavigationLinks = (
  drug: DrugItem
): InstructionOption[] => {
  const intl = useIntl();
  return [
    {
      name: intl.formatMessage(trns.packaging),
      href: '',
      id: 'packaging',
      value: drug?.packaging
    },
    {
      name: intl.formatMessage(trns.instructionForm),
      href: '',
      id: 'instructionForm',
      value: drug?.instructionForm
    },
    {
      name: intl.formatMessage(trns.pharmacodinamica),
      href: '',
      id: 'pharmacodinamica',
      value: drug?.pharmacodinamica
    },
    {
      name: intl.formatMessage(trns.pharmacokinetiks),
      href: '',
      id: 'pharmacokinetiks',
      value: drug?.pharmacokinetiks
    },
    {
      name: intl.formatMessage(trns.pharmGroup),
      href: '',
      id: 'pharmGroup',
      value: drug?.pharmGroup
    },
    {
      name: intl.formatMessage(trns.interactions),
      href: '',
      id: 'interactions',
      value: drug?.interactions
    },
    {
      name: intl.formatMessage(trns.composition),
      href: '',
      id: 'composition',
      value: drug?.composition
    },
    {
      name: intl.formatMessage(trns.reactionSpeed),
      href: '',
      id: 'reactionSpeed',
      value: drug?.reactionSpeed
    },
    {
      name: intl.formatMessage(trns.pregnancy),
      href: '',
      id: 'pregnancy',
      value: drug?.pregnancy
    },
    {
      name: intl.formatMessage(trns.children),
      href: '',
      id: 'children',
      value: drug?.children
    },
    {
      name: intl.formatMessage(trns.indications),
      href: '',
      id: 'indications',
      value: drug?.indications
    },
    {
      name: intl.formatMessage(trns.contraindications),
      href: '',
      id: 'contraindications',
      value: drug?.contraindications
    },
    {
      name: intl.formatMessage(trns.application),
      href: '',
      id: 'application',
      value: drug?.application
    },
    {
      name: intl.formatMessage(trns.sideEffects),
      href: '',
      id: 'sideEffects',
      value: drug?.sideEffects
    },
    {
      name: intl.formatMessage(trns.overdose),
      href: '',
      id: 'overdose',
      value: drug?.overdose
    },
    {
      name: intl.formatMessage(trns.storageConditions),
      href: '',
      id: 'storageConditions',
      value: drug?.storageConditions
    },
    {
      name: intl.formatMessage(trns.description),
      href: '',
      id: 'description',
      value: drug?.description
    }
  ].filter((n) => n.value);
};

export const generateFAQList = (drug: DrugItem) => {
  const intl = useIntl();
  const prescriptionNeeded =
    drug?.withRecipe?.color === 'GREEN' ? (
      <FormattedMessage {...trns.releasedWithoutRecipe} />
    ) : (
      <FormattedMessage {...trns.releasedWithRecipe} />
    );
  return [
    {
      name: intl.formatMessage(trns.priceTitle, {
        drugName: drug?.name
      }),
      id: 'minPrice',
      value: drug?.minPrice,
      bodyText: (
        <FormattedMessage
          {...trns.priceContent}
          values={{
            drugName: drug?.name,
            minPrice: drug?.minPrice,
            maxPrice: drug?.maxPrice
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.componentTitle, {
        drugName: drug?.name
      }),
      id: 'component',
      value: drug?.component,
      bodyText: (
        <FormattedMessage
          {...trns.componentContent}
          values={{
            drugName: drug?.name,
            active: drug?.component
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.withRecipeTitle, {
        drugName: drug?.name
      }),
      id: 'withRecipe',
      value: drug?.withRecipe,
      bodyText: (
        <FormattedMessage
          {...trns.withRecipeContent}
          values={{
            drugName: drug?.name,
            prescriptionNeeded,
            withPrescription: drug?.withRecipe?.text
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.forDriversTitle, {
        drugName: drug?.name
      }),
      id: 'forDrivers',
      value: drug?.forDrivers,
      bodyText: (
        <FormattedMessage
          {...trns.forDriversContent}
          values={{
            drugName: drug?.name,
            allowed: drug?.forDrivers?.text
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.forPregnantTitle, {
        drugName: drug?.name
      }),
      id: 'forPregnant',
      value: drug?.forPregnant,
      bodyText: (
        <FormattedMessage
          {...trns.forPregnantContent}
          values={{
            drugName: drug?.name,
            allowed: drug?.forPregnant?.text
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.forMothersTitle, {
        drugName: drug?.name
      }),
      id: 'forMothers',
      value: drug?.forMothers,
      bodyText: (
        <FormattedMessage
          {...trns.forMothersContent}
          values={{
            drugName: drug?.name,
            allowed: drug?.forMothers?.text
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.forAllergicTitle, {
        drugName: drug?.name
      }),
      id: 'forAllergic',
      value: drug?.forAllergic,
      bodyText: (
        <FormattedMessage
          {...trns.forAllergicContent}
          values={{
            drugName: drug?.name,
            allowed: drug?.forAllergic?.text
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.forDiabeticTitle, {
        drugName: drug?.name
      }),
      id: 'forDiabetic',
      value: drug?.forDiabetic,
      bodyText: (
        <FormattedMessage
          {...trns.forDiabeticContent}
          values={{
            drugName: drug?.name,
            allowed: drug?.forDiabetic?.text
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.storageTemperatureTitle, {
        drugName: drug?.name
      }),
      id: 'storageTemperature',
      value: drug?.storageTemperature,
      bodyText: (
        <FormattedMessage
          {...trns.storageTemperatureContent}
          values={{
            drugName: drug?.name,
            temperature: drug?.storageTemperature?.text
          }}
        />
      )
    },
    {
      name: intl.formatMessage(trns.expirationTitle, {
        drugName: drug?.name
      }),
      id: 'expiration',
      value: drug?.expiration,
      bodyText: (
        <FormattedMessage
          {...trns.expirationContent}
          values={{
            drugName: drug?.name,
            expiration: drug?.expiration
          }}
        />
      )
    }
  ].filter((n) => n.value);
};

export enum TabTypes {
  ABOUT_PRODUCT = 'aboutProduct',
  PRICES = 'prices',
  INSTRUCTIONS = 'instructions',
  CHARACTERISTICS = 'characteristics',
  ANALOGUE = 'analogi',
  REVIEWS = 'reviews'
}
